/* SellerWms — configuration page for the ERP / WMS Connector app.
   Seller picks a vendor, drops in their endpoint + API key, tests the
   connection, and decides whether to auto-forward every incoming PO.
   Forward history is shown at the bottom so the seller can spot failures. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Warehouse, Plug, CheckCircle2, XCircle, PlayCircle, Trash2, RefreshCw,
  AlertTriangle, ShieldCheck, ExternalLink,
} from "lucide-react";

const VENDORS = [
  { value: "custom_webhook", label: "Custom webhook",           auth: "bearer", desc: "Point at any HTTPS endpoint that accepts JSON (POST)." },
  { value: "shiphero",       label: "ShipHero",                 auth: "bearer", desc: "Modern cloud WMS for DTC + wholesale operations." },
  { value: "shipbob",        label: "ShipBob",                  auth: "bearer", desc: "Outsourced fulfilment for e-comm brands." },
  { value: "extensiv",       label: "3PL Central (Extensiv)",   auth: "bearer", desc: "Warehouse management for 3PL operators." },
  { value: "netsuite",       label: "NetSuite (REST)",          auth: "bearer", desc: "Oracle NetSuite cloud ERP." },
  { value: "sap",            label: "SAP Business One",         auth: "basic",  desc: "SAP B1 Service Layer — use user:password in API key." },
  { value: "odoo",           label: "Odoo",                     auth: "basic",  desc: "Odoo ERP — user:password for API key." },
  { value: "zoho",           label: "Zoho Inventory",           auth: "bearer", desc: "Zoho Inventory REST API." },
];

export default function SellerWms() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);      // null while loading, {} if none
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);

  // Form fields — start empty; populated from saved cfg on load
  const [form, setForm] = useState({
    vendor: "custom_webhook",
    endpoint_url: "",
    api_key: "",
    auto_forward: true,
    pick_list_only: false,
  });

  const load = async () => {
    try {
      const { data } = await api.get("/seller/wms");
      setCfg(data || null);
      if (data) {
        setForm({
          vendor: data.vendor || "custom_webhook",
          endpoint_url: data.endpoint_url || "",
          api_key: "",   // never prefill — it's masked server-side
          auto_forward: data.auto_forward ?? true,
          pick_list_only: data.pick_list_only ?? false,
        });
      }
      setLoaded(true);
    } catch {
      setCfg(null);
      setLoaded(true);
    }
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/seller/wms/logs?limit=25");
      setLogs(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadLogs(); }, []);

  const vendorMeta = VENDORS.find(v => v.value === form.vendor) || VENDORS[0];
  const hasSavedKey = !!cfg?.api_key_hint;

  const save = async () => {
    if (!form.endpoint_url.trim()) return toast.error("Endpoint URL is required");
    if (!/^https?:\/\//i.test(form.endpoint_url)) return toast.error("Endpoint must start with https://");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.api_key) delete body.api_key;  // keep the existing one server-side
      await api.post("/seller/wms", body);
      toast.success(cfg ? "WMS configuration updated" : "WMS connected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/seller/wms/test");
      if (data.ok) toast.success(`Ping succeeded — HTTP ${data.status_code}`);
      else toast.error(`Ping failed${data.status_code ? ` — HTTP ${data.status_code}` : ""}${data.error ? `: ${data.error}` : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WMS? This also uninstalls the app — orders will stop auto-forwarding.")) return;
    try {
      await api.delete("/seller/wms");
      toast.success("WMS disconnected");
      nav("/seller/apps");
    } catch {
      toast.error("Could not disconnect");
    }
  };

  if (!loaded) {
    return (
      <div className="p-10 text-sm text-foreground/55">Loading ERP / WMS connector…</div>
    );
  }

  return (
    <div data-testid="seller-wms">
      <PageHeader
        title="ERP / WMS Connector"
        subtitle="Automatically forward every incoming PO and pick list to your existing ERP or warehouse management system."
        action={cfg && (
          <Button onClick={disconnect} variant="outline" data-testid="wms-disconnect" className="rounded-full">
            <Trash2 className="w-4 h-4 mr-1.5"/>Disconnect
          </Button>
        )}
      />

      <div className="p-8 max-w-3xl space-y-6">

        {/* Status card */}
        <div className="rounded-2xl border border-border bg-white p-5 flex items-center gap-4" data-testid="wms-status-card">
          <div className="w-12 h-12 rounded-xl bg-foreground text-accent grid place-items-center shrink-0">
            <Warehouse className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="display text-lg">
              {cfg
                ? (cfg.auto_forward
                    ? `Connected to ${VENDORS.find(v => v.value === cfg.vendor)?.label || cfg.vendor}`
                    : `${VENDORS.find(v => v.value === cfg.vendor)?.label || cfg.vendor} · forwarding paused`)
                : "Not connected"}
            </div>
            <div className="text-[12px] text-foreground/60 truncate mt-0.5">
              {cfg ? cfg.endpoint_url : "Configure below to start auto-forwarding sales orders to your warehouse."}
            </div>
            {cfg?.last_test && (
              <div className={`text-[11px] mt-1.5 inline-flex items-center gap-1.5 ${cfg.last_test.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="wms-last-test">
                {cfg.last_test.ok ? <CheckCircle2 className="w-3 h-3"/> : <XCircle className="w-3 h-3"/>}
                Last ping {cfg.last_test.ok ? "succeeded" : "failed"} · {new Date(cfg.last_test.at).toLocaleString()}
              </div>
            )}
          </div>
          {cfg && (
            <Button onClick={runTest} disabled={testing} data-testid="wms-test" variant="outline" className="rounded-full shrink-0">
              <PlayCircle className="w-4 h-4 mr-1.5"/>{testing ? "Testing…" : "Test ping"}
            </Button>
          )}
        </div>

        {/* Vendor picker */}
        <div className="rounded-2xl border border-border bg-white p-6">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3">Vendor</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="wms-vendor-grid">
            {VENDORS.map(v => (
              <button
                key={v.value}
                onClick={() => setForm({ ...form, vendor: v.value })}
                data-testid={`wms-vendor-${v.value}`}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  form.vendor === v.value
                    ? "border-foreground bg-foreground/[0.03]"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <div className="text-sm font-medium">{v.label}</div>
                <div className="text-[10px] uppercase tracking-wider text-foreground/50 mt-1">
                  {v.auth === "bearer" ? "Bearer token" : "Basic auth"}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-3 text-[12px] text-foreground/60 flex items-start gap-2">
            <Plug className="w-3.5 h-3.5 mt-0.5 shrink-0 text-foreground/45"/>
            <span>{vendorMeta.desc}</span>
          </div>
        </div>

        {/* Endpoint + credentials */}
        <div className="rounded-2xl border border-border bg-white p-6 space-y-4">
          <div>
            <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">Endpoint URL</Label>
            <Input
              value={form.endpoint_url}
              onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })}
              placeholder="https://api.your-wms.com/webhooks/orders"
              data-testid="wms-endpoint"
              className="h-11 mt-1.5 mono text-sm"
            />
            <p className="text-[11px] text-foreground/50 mt-1.5">
              We POST a JSON payload here every time a PO lands. Must be HTTPS.
            </p>
          </div>

          <div>
            <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">
              {vendorMeta.auth === "basic" ? "Credentials (user:password)" : "API key / Bearer token"}
            </Label>
            <Input
              type="password"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder={hasSavedKey ? `Saved · ${cfg.api_key_hint} — leave blank to keep` : (vendorMeta.auth === "basic" ? "user:password" : "sk_live_…")}
              data-testid="wms-api-key"
              className="h-11 mt-1.5 mono text-sm"
            />
            {hasSavedKey && (
              <p className="text-[11px] text-foreground/50 mt-1.5 inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3"/>Key saved · only you can rotate it. Leave blank to keep the existing one.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <div className="text-sm font-medium">Auto-forward new orders</div>
              <div className="text-[11.5px] text-foreground/55 mt-0.5">Every incoming PO triggers a forward to your endpoint.</div>
            </div>
            <Switch
              checked={form.auto_forward}
              onCheckedChange={(v) => setForm({ ...form, auto_forward: v })}
              data-testid="wms-auto-forward"
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t border-border">
            <div>
              <div className="text-sm font-medium">Pick-list mode</div>
              <div className="text-[11.5px] text-foreground/55 mt-0.5">Strip pricing and invoice data — send SKU + quantity only.</div>
            </div>
            <Switch
              checked={form.pick_list_only}
              onCheckedChange={(v) => setForm({ ...form, pick_list_only: v })}
              data-testid="wms-picklist-only"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="wms-save"
              className="flex-1 h-11 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              {saving ? "Saving…" : cfg ? "Save changes" : "Connect WMS"}
            </Button>
            {cfg && (
              <Button
                onClick={runTest}
                disabled={testing}
                variant="outline"
                className="h-11 rounded-full"
                data-testid="wms-save-and-test"
              >
                <PlayCircle className="w-4 h-4 mr-1.5"/>
                Test
              </Button>
            )}
          </div>
        </div>

        {/* Forward history */}
        <div className="rounded-2xl border border-border bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Recent forwards</div>
            <button
              onClick={loadLogs}
              data-testid="wms-refresh-logs"
              className="text-[11px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3"/>Refresh
            </button>
          </div>
          <div className="mt-3 -mx-3" data-testid="wms-logs">
            {logs.length === 0 ? (
              <div className="text-center py-10 text-xs text-foreground/50">
                No forwards yet. They'll appear here the moment a buyer places an order or uploads a PO.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {logs.map(l => (
                  <div key={l.id} className="flex items-center gap-3 px-3 py-3" data-testid={`wms-log-${l.id}`}>
                    {l.status === "ok"
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0"/>
                      : <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0"/>}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {l.order_code || "—"} <span className="text-foreground/50 font-normal">· {l.event}</span>
                      </div>
                      <div className="text-[11px] text-foreground/55 mt-0.5 truncate">
                        {l.status === "ok"
                          ? `HTTP ${l.response_code || "—"}`
                          : (l.error || `HTTP ${l.response_code || "—"} · ${(l.response || "").slice(0, 120)}`)}
                      </div>
                    </div>
                    <div className="text-[10px] mono text-foreground/45 shrink-0">
                      {new Date(l.at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-[11px] text-foreground/45 flex items-center gap-1">
          <ExternalLink className="w-3 h-3"/>
          Canonical payload schema is vendor-agnostic. Need custom mapping? Use the custom webhook + a thin proxy (e.g. n8n, Zapier).
        </div>
      </div>
    </div>
  );
}
